import React, { useEffect, useId, useRef, useState } from 'react';
import { cx } from '../cx';

// D-09 §15.36 — Combobox (Material Exposed Dropdown Menu 패턴)
// .field > label + .combo > .input(트리거) + .menu(§15.11 재사용) > .menu-item
// - options: 정적 목록 (searchable 이면 타이핑 로컬 필터)
// - loadOptions: 비동기 로더 — 로딩 전에는 displayValue 라벨을 그대로 유지(선택값 소실 방지)
export interface ComboboxOption {
  value: string;
  label: string;
  /** true 면 목록에 보이되 선택 불가(비활성) — MultiSelect에서 지원 */
  disabled?: boolean;
}

export interface ComboboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'size'> {
  value: string;
  onChange: (value: string, option: ComboboxOption) => void;
  options?: ComboboxOption[];
  loadOptions?: (query: string) => Promise<ComboboxOption[]>;
  /** 현재 value 의 표시 라벨 — 옵션 목록이 아직 로딩되지 않아도 이 라벨을 보여준다 */
  displayValue?: string;
  /** true 면 타이핑 필터(오토컴플리트), false 면 읽기전용 트리거(셀렉트형) */
  searchable?: boolean;
  label?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg';
  emptyText?: string;
  /** 옵션 메뉴 항목을 커스텀 렌더(배지·2줄 지표 등). 미지정 시 opt.label 텍스트. 소비자는 opt.value로
   *  자체 데이터를 찾아 리치하게 렌더한다. 필터(searchable)는 여전히 opt.label 기준으로 동작한다. */
  renderOption?: (option: ComboboxOption) => React.ReactNode;
}

export const Combobox = React.forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(
  {
    value,
    onChange,
    options,
    loadOptions,
    displayValue,
    searchable = false,
    label,
    error,
    size = 'md',
    placeholder,
    emptyText = '항목이 없습니다',
    renderOption,
    disabled,
    className = '',
    id,
    ...props
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<ComboboxOption[]>(options ?? []);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const seqRef = useRef(0); // 비동기 응답 역전(레이스) 가드
  const listId = useId();

  const filter = (list: ComboboxOption[], q: string) =>
    q ? list.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : list;

  const fetchItems = (q: string) => {
    if (loadOptions) {
      const seq = ++seqRef.current;
      setLoading(true);
      loadOptions(q)
        .then((list) => {
          if (seq !== seqRef.current) return; // 늦게 도착한 이전 요청 무시
          setItems(list);
          setLoading(false);
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setItems([]);
          setLoading(false);
        });
    } else {
      setItems(filter(options ?? [], q));
    }
  };

  const openMenu = () => {
    if (disabled || open) return;
    setOpen(true);
    setQuery('');
    setActiveIndex(-1);
    fetchItems('');
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const select = (opt: ComboboxOption) => {
    onChange(opt.value, opt);
    closeMenu();
  };

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) closeMenu();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // 열 때 선택 항목을 목록 가운데로 스크롤(D-09 §15.36) — 아래쪽에 있어도 바로 보이게.
  // scrollIntoView 는 페이지까지 같이 움직여 화면이 흔들리므로 목록의 scrollTop 만 옮긴다. 비동기 목록은 로딩 완료 후.
  useEffect(() => {
    const menu = menuRef.current;
    const item = menu?.querySelector<HTMLElement>('.menu-item.selected');
    if (open && !loading && menu && item) menu.scrollTop = item.offsetTop - menu.clientHeight / 2 + item.offsetHeight / 2;
  }, [open, loading]);

  // 정적 옵션이 바뀌면 목록 동기화 (열려 있으면 현재 검색어로 재필터)
  useEffect(() => {
    if (!loadOptions) setItems(filter(options ?? [], open ? query : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return openMenu();
      setActiveIndex((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && items[activeIndex]) {
        e.preventDefault();
        select(items[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        closeMenu();
      }
    }
  };

  // 표시 라벨: 로딩된 목록 → 정적 옵션 → displayValue → 원값 순으로 해석
  const selectedLabel =
    (options ?? items).find((o) => o.value === value)?.label ?? displayValue ?? value;
  const inputValue = open && searchable ? query : selectedLabel;

  return (
    <div className={cx('field', error && 'error', className)}>
      {label && <label htmlFor={id}>{label}</label>}
      <div className={cx('combo', open && 'open')} ref={rootRef}>
        <input
          id={id}
          ref={ref}
          className={cx('input', size !== 'md' && `input-${size}`)}
          {...props}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete={searchable ? 'list' : 'none'}
          readOnly={!searchable || !open}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onClick={openMenu}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(-1);
            fetchItems(e.target.value);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="caret" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M4 6L8 10L12 6" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {open && (
          <div className="menu show" role="listbox" id={listId} ref={menuRef}>
            {loading && <div className="combo-loading">불러오는 중…</div>}
            {!loading && items.length === 0 && <div className="combo-empty">{emptyText}</div>}
            {!loading &&
              items.map((opt, i) => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  className={cx('menu-item', opt.value === value && 'selected', i === activeIndex && 'active')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => select(opt)}
                  onMouseEnter={() => setActiveIndex(i)}
                >
                  {renderOption ? renderOption(opt) : opt.label}
                </div>
              ))}
          </div>
        )}
      </div>
      {error && <span className="help">{error}</span>}
    </div>
  );
});
